import React, { useEffect, useMemo } from 'react';

import { useAISmartButtonActions } from '@atlassian/ai-smart-button-api';
import type { ConversationAction } from '@atlassian/rovo-conversation-actions';

import { useAudioButtonWrapperStore } from '@confluence/audio/entry-points/useAudioButtonWrapperStore.tsx';
import { CommentCountsProvider } from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import type {
	PageRovoConfigProviderProps,
	RovoConfiguration,
	RovoConfigurationProviderProps,
} from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.ts';
import { useViewPageRovoActions } from '@confluence/page-rovo-config-common/entry-points/useViewPageRovoActions.ts';
import { useRovoConfigProviderProps } from '@confluence/rovo-config-provider-common/entry-points/useRovoConfigProviderProps.ts';

import { useNudgeRecommendationFilter } from './hooks/useNudgeRecommendationFilter.tsx';

const ViewPageConfigProviderInner = ({ setConfiguration }: RovoConfigurationProviderProps) => {
	const { contentType, contentId } = useRovoConfigProviderProps() as PageRovoConfigProviderProps;

	const viewPageRovoActions = useViewPageRovoActions();
	const enableNudgeCheck = useNudgeRecommendationFilter(contentType, contentId);
	const { setNudges } = useAISmartButtonActions();
	const { eligibleForChangeboarding } = useAudioButtonWrapperStore();

	const config = useMemo<RovoConfiguration>(() => {
		return {
			getActions: async () => {
				const actions = viewPageRovoActions.map(
					(action: { action: ConversationAction }) => action.action,
				);
				return Promise.resolve(actions);
			},
		};
	}, [viewPageRovoActions]);

	useEffect(() => {
		setConfiguration(config);

		if (!enableNudgeCheck) {
			setNudges([]);
			return;
		}

		const nudges = [
			viewPageRovoActions.find((a: { actionId: string }) => a.actionId === 'page-catchup')?.action,
			// Only show audio briefing nudge if the user is not eligible for audio briefing changeboarding
			!eligibleForChangeboarding &&
				viewPageRovoActions.find((a: { actionId: string }) => a.actionId === 'audio-briefing')
					?.action,
		].filter(Boolean) as ConversationAction[];

		if (nudges.length > 0) {
			setNudges(nudges);
		}
	}, [
		config,
		setConfiguration,
		enableNudgeCheck,
		viewPageRovoActions,
		setNudges,
		eligibleForChangeboarding,
	]);

	return null;
};

export const ViewPageRovoConfigProvider = (props: RovoConfigurationProviderProps) => {
	const { contentId } = useRovoConfigProviderProps() as PageRovoConfigProviderProps;
	return (
		<CommentCountsProvider contentId={contentId}>
			<ViewPageConfigProviderInner {...props} />
		</CommentCountsProvider>
	);
};
